import { NextResponse, userAgent as getUserAgent } from 'next/server';
import { getClientIp } from '@zmyle/webkit-http-utils';
import { tooManyRequestsError } from 'server/middlewares/rateLimiters/helpers';
import {
  captureException,
  formatDateAsZmyleApi,
  isZmyleError,
  isAxiosError,
  AppRateLimiterResponse,
  isAppRateLimiterResponse,
} from 'utils/helpers';
import { AppHeaders, ZmyleErrorId } from 'utils/constants';

import { getHostname } from './helpers';

export const shouldRenameError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const errorNames = [
    'Error',
    'TypeError',
    'AxiosError',
    'UnknownError',
    'UnhandledPromiseRejectionError',
  ];

  return errorNames.includes(error.name);
};

export const isRateLimited = (payload: AppRateLimiterResponse): boolean => {
  return (
    payload.rateLimiter.clientIP?.remainingPoints === 0 ||
    payload.rateLimiter.giftcardId?.remainingPoints === 0
  );
};

export const isNextRedirectError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  return (
    error.message === 'NEXT_REDIRECT' ||
    error.message === 'NEXT_HTTP_ERROR_FALLBACK;404'
  );
};

interface GenerateErrorReportPayloadParams {
  req?: Request;
  headers?: Headers;
}

interface GenerateMetadata {
  clientIP: string;
  userAgent: string | undefined | ReturnType<typeof getUserAgent>;
  giftcardId: string | null;
  hostname: string;
  urlPath: string | undefined | null;
}

export const generateErrorReportMetadata = (
  params: GenerateErrorReportPayloadParams,
): GenerateMetadata | object => {
  const { req, headers } = params;

  const normalizeUrl = (url: string | null): string => {
    if (!url) return '';

    return url.replace('https://localhost:3000', '');
  };

  if (req instanceof Request) {
    const clientIP = getClientIp(req.headers);
    const userAgent = getUserAgent(req);
    const requestedUrl = normalizeUrl(
      req.url || req.headers.get(AppHeaders.REQUESTED_URL),
    );
    const browserUrl = normalizeUrl(req.headers.get(AppHeaders.BROWSER_URL));
    const hostname = getHostname(req.headers);
    const referer = req.headers.get('referer');

    return { clientIP, userAgent, hostname, requestedUrl, browserUrl, referer };
  }

  if (headers) {
    const clientIP = getClientIp(headers);
    const userAgent = getUserAgent({ headers });
    const requestedUrl = normalizeUrl(headers.get(AppHeaders.REQUESTED_URL));
    const browserUrl = normalizeUrl(headers.get(AppHeaders.BROWSER_URL));
    const hostname = getHostname(headers);
    const referer = headers.get('referer');

    return { clientIP, userAgent, hostname, requestedUrl, browserUrl, referer };
  }

  return {};
};

interface ApiErrorHandlerParams {
  req: Request;
  error: unknown;
  responseHeaders?: Headers;
  metadata?: Record<string, any>;
}

export const apiErrorHandler = (params: ApiErrorHandlerParams) => {
  const { req, responseHeaders, error, metadata } = params;

  const errorMetadata = generateErrorReportMetadata({ req });

  const _metadata = { ...metadata, ...errorMetadata };

  if (
    (isAppRateLimiterResponse(error) && isRateLimited(error)) ||
    (isZmyleError(error) && error.status === tooManyRequestsError.status)
  ) {
    captureException({
      error,
      tags: { rateLimit: 'true' },
      metadata: _metadata,
    });

    const tooManyRequests: ZmyleError = {
      ...tooManyRequestsError,
      occurredAt: formatDateAsZmyleApi(new Date()),
    };

    return NextResponse.json(tooManyRequests, {
      headers: responseHeaders,
      status: tooManyRequests.status,
    });
  }

  const _error = isAppRateLimiterResponse(error) ? error.reason : error;

  captureException({
    error: _error,
    metadata: _metadata,
    tags: { method: 'apiErrorHandler' },
  });

  if (isAxiosError(_error) && _error.response) {
    if (_error.response?.data && isZmyleError(_error.response?.data)) {
      const { data } = _error.response;

      return NextResponse.json(data, {
        status: data.status,
        headers: responseHeaders,
      });
    }
  }

  if (isZmyleError(_error)) {
    return NextResponse.json(_error, {
      status: _error.status,
      headers: responseHeaders,
    });
  }

  return NextResponse.json(
    { error: 'Internal server error' },
    { status: 500, headers: responseHeaders },
  );
};

interface ServerComponentErrorHandlerParams {
  error: unknown;
  headers: Headers;
  tags?: Record<string, string>;
}

export const serverComponentErrorHandler = (
  params: ServerComponentErrorHandlerParams,
): ZmyleError => {
  const { error, headers, tags = {} } = params;

  if (isNextRedirectError(error)) throw error;

  // axios errors are being reported by its interceptor
  if (isAxiosError(error)) {
    if (error.response?.data && isZmyleError(error.response?.data)) {
      return error.response.data;
    }
  }

  const metadata = generateErrorReportMetadata({ headers });

  captureException({
    error,
    metadata,
    tags: { ...tags, source: 'server component' },
  });

  if (isZmyleError(error)) return error;

  const internalServerError: ZmyleError = {
    errorId: ZmyleErrorId.INTERNAL_SERVER_ERROR,
    errorMsg: 'Internal server error',
    occurredAt: formatDateAsZmyleApi(new Date()),
    status: 500,
  };

  return internalServerError;
};
